import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validateRegisterForm } from '../../utils/validation';
import './RegisterForm.css';

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  storeName: string;
}

const RegisterForm = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState<RegisterFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    storeName: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validation = validateRegisterForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined,
        storeName: formData.storeName || undefined,
      });

      if (result.success) {
        // v0.10.1: Redirect to member center (verification link sent via email)
        navigate('/member');
      } else {
        setSubmitError(result.error || 'Registration failed. Please try again.');
      }
    } catch {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-form">
      <div className="register-form__header">
        <h2 className="register-form__title">會員註冊</h2>
        <p className="register-form__subtitle">建立帳號以發布廣告和聯繫許願</p>
      </div>

      <form onSubmit={handleSubmit} className="register-form__form">
        {submitError && (
          <div className="register-form__error" role="alert">
            {submitError}
          </div>
        )}

        <div className="register-form__field">
          <label htmlFor="name" className="register-form__label">
            姓名 *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`register-form__input ${
              errors.name ? 'register-form__input--error' : ''
            }`}
            placeholder="請輸入姓名"
            disabled={isSubmitting}
            autoComplete="name"
          />
          {errors.name && (
            <span className="register-form__field-error">
              {errors.name}
            </span>
          )}
        </div>

        <div className="register-form__field">
          <label htmlFor="email" className="register-form__label">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`register-form__input ${
              errors.email ? 'register-form__input--error' : ''
            }`}
            placeholder="your@email.com"
            disabled={isSubmitting}
            autoComplete="email"
          />
          {errors.email && (
            <span className="register-form__field-error">{errors.email}</span>
          )}
        </div>

        <div className="register-form__field">
          <label htmlFor="password" className="register-form__label">
            密碼 *
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={`register-form__input ${
              errors.password ? 'register-form__input--error' : ''
            }`}
            placeholder="至少 6 個字元"
            disabled={isSubmitting}
            autoComplete="new-password"
          />
          {errors.password && (
            <span className="register-form__field-error">{errors.password}</span>
          )}
        </div>

        <div className="register-form__field">
          <label htmlFor="confirmPassword" className="register-form__label">
            確認密碼 *
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={`register-form__input ${
              errors.confirmPassword ? 'register-form__input--error' : ''
            }`}
            placeholder="再次輸入密碼"
            disabled={isSubmitting}
            autoComplete="new-password"
          />
          {errors.confirmPassword && (
            <span className="register-form__field-error">
              {errors.confirmPassword}
            </span>
          )}
        </div>

        <div className="register-form__field">
          <label htmlFor="phone" className="register-form__label">
            電話
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`register-form__input ${
              errors.phone ? 'register-form__input--error' : ''
            }`}
            placeholder="0912-345-678"
            disabled={isSubmitting}
            autoComplete="tel"
          />
          {errors.phone && (
            <span className="register-form__field-error">{errors.phone}</span>
          )}
        </div>

        <div className="register-form__field">
          <label htmlFor="storeName" className="register-form__label">
            公司名稱
          </label>
          <input
            type="text"
            id="storeName"
            name="storeName"
            value={formData.storeName}
            onChange={handleChange}
            className={`register-form__input ${
              errors.storeName ? 'register-form__input--error' : ''
            }`}
            placeholder="請輸入公司名稱（選填）"
            disabled={isSubmitting}
            autoComplete="organization"
          />
          {errors.storeName && (
            <span className="register-form__field-error">
              {errors.storeName}
            </span>
          )}
        </div>

        <button
          type="submit"
          className="register-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? '註冊中...' : '註冊'}
        </button>

        <div className="register-form__footer">
          已經是會員？{' '}
          <a href="/login" className="register-form__link">
            立即登入
          </a>
        </div>
      </form>
    </div>
  );
};

export default RegisterForm;
